import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import heic2any from "heic2any";
import { heicTo } from "heic-to";
import JSZip from "jszip";
import confetti from "canvas-confetti";
import "./App.css";
import ContactModal from "./ContactModal";

// Types
interface ProcessedImage {
  id: string;
  file: File;
  imageBlob: Blob;
  preview: string;
  width: number;
  height: number;
  originalName: string;
}

type OutputFormat = "jpeg" | "png" | "webp";

function App() {
  const APP_VERSION = "1.5.2"; // Update this when you make changes
  const [portraitImages, setPortraitImages] = useState<ProcessedImage[]>([]);
  const [landscapeImages, setLandscapeImages] = useState<ProcessedImage[]>([]);
  const [portraitHeight, setPortraitHeight] = useState<number>(0);
  const [landscapeWidth, setLandscapeWidth] = useState<number>(0);
  const [outputFormats, setOutputFormats] = useState<OutputFormat[]>([]);
  const [quality, setQuality] = useState<number>(85);
  const [preserveFilenames, setPreserveFilenames] = useState<boolean>(false);
  const [customPrefix, setCustomPrefix] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [showUpdateNotice, setShowUpdateNotice] = useState<boolean>(false);
  const [showContactModal, setShowContactModal] = useState<boolean>(false);
  const [estimatedSizes, setEstimatedSizes] = useState<Map<string, { format: string; size: number }[]>>(new Map());
  const [isEstimating, setIsEstimating] = useState<boolean>(false);
  const estimateTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check for version update on mount and periodically
  React.useEffect(() => {
    const checkVersion = () => {
      const storedVersion = localStorage.getItem("appVersion");
      if (storedVersion && storedVersion !== APP_VERSION) {
        setShowUpdateNotice(true);
      } else if (!storedVersion) {
        localStorage.setItem("appVersion", APP_VERSION);
      }
    };

    // Check immediately on mount
    checkVersion();

    // Check every 10 seconds for updates
    const interval = setInterval(checkVersion, 10 * 1000);

    // Check when tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkVersion();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Debounced file size estimation
  useEffect(() => {
    if (estimateTimerRef.current) {
      clearTimeout(estimateTimerRef.current);
    }

    const allImages = [
      ...portraitImages.map(img => ({ ...img, targetDim: portraitHeight, type: 'portrait' as const })),
      ...landscapeImages.map(img => ({ ...img, targetDim: landscapeWidth, type: 'landscape' as const }))
    ];

    // Only estimate if we have images, formats, and dimensions
    const hasPortraitReady = portraitImages.length > 0 && portraitHeight > 0;
    const hasLandscapeReady = landscapeImages.length > 0 && landscapeWidth > 0;
    if (outputFormats.length === 0 || (!hasPortraitReady && !hasLandscapeReady)) {
      setEstimatedSizes(new Map());
      setIsEstimating(false);
      return;
    }

    setIsEstimating(true);

    estimateTimerRef.current = setTimeout(async () => {
      const newSizes = new Map<string, { format: string; size: number }[]>();

      for (const imgData of allImages) {
        // Skip if this image's dimension isn't set
        if (imgData.targetDim === 0) continue;

        try {
          const { img } = await loadImage(imgData.imageBlob);

          // Calculate target dimensions
          let newWidth: number, newHeight: number;
          if (imgData.type === 'portrait') {
            const scale = imgData.targetDim / img.height;
            newHeight = imgData.targetDim;
            newWidth = Math.round(img.width * scale);
          } else {
            const scale = imgData.targetDim / img.width;
            newWidth = imgData.targetDim;
            newHeight = Math.round(img.height * scale);
          }

          // Create canvas at target size
          const canvas = document.createElement('canvas');
          canvas.width = newWidth;
          canvas.height = newHeight;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, newWidth, newHeight);
          URL.revokeObjectURL(img.src);

          // Estimate size for each format
          const formatSizes: { format: string; size: number }[] = [];
          for (const format of outputFormats) {
            const mimeType = `image/${format}`;
            const qualityValue = format === 'png' ? 1 : quality / 100;
            const blob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob((b) => {
                if (b) resolve(b);
                else reject(new Error('Failed to estimate'));
              }, mimeType, qualityValue);
            });
            formatSizes.push({ format: format.toUpperCase(), size: blob.size });
          }

          newSizes.set(imgData.id, formatSizes);
        } catch (err) {
          console.warn(`Failed to estimate size for ${imgData.originalName}:`, err);
        }
      }

      setEstimatedSizes(newSizes);
      setIsEstimating(false);
    }, 500);

    return () => {
      if (estimateTimerRef.current) {
        clearTimeout(estimateTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portraitImages, landscapeImages, portraitHeight, landscapeWidth, outputFormats, quality]);

  const handleReload = (e: React.MouseEvent) => {
    // Get the button's position on the page
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    // Small confetti burst from the button with fade out
    confetti({
      particleCount: 30,
      spread: 60,
      origin: { x, y },
      startVelocity: 20,
      gravity: 1,
      decay: 0.91,
      ticks: 150,
      colors: ["#CC4106", "#4ECDC4", "#1A2332", "#FFD700", "#FF69B4"],
      disableForReducedMotion: true,
      drift: 0,
      scalar: 1,
      shapes: ["circle", "square"],
      zIndex: 10000,
    });

    // Wait for confetti animation, then reload
    setTimeout(() => {
      localStorage.setItem("appVersion", APP_VERSION);
      window.location.reload();
    }, 1500);
  };

  // Toggle format selection
  const toggleFormat = (format: OutputFormat) => {
    setOutputFormats((prev) => {
      if (prev.includes(format)) {
        // Don't allow deselecting if it's the only one selected
        if (prev.length === 1) return prev;
        return prev.filter((f) => f !== format);
      } else {
        return [...prev, format];
      }
    });
  };

  // Extract first frame from video file
  const extractVideoFrame = async (file: File): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        video.currentTime = 0;
      };

      video.onseeked = () => {
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(video.src);
          resolve(canvas);
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error("Error loading video"));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  // Convert HEIC to blob
  const convertHeicToBlob = async (file: File): Promise<Blob> => {
    // Try heic-to library first (newer, better support)
    try {
      console.log(
        "Attempting HEIC conversion with heic-to library:",
        file.name,
        "Size:",
        file.size,
      );
      const convertedBlob = await heicTo({
        blob: file,
        type: "image/jpeg",
        quality: 0.9,
      });
      console.log(
        "heic-to conversion successful, blob size:",
        convertedBlob.size,
      );
      return convertedBlob;
    } catch (error: any) {
      console.warn("heic-to conversion failed, trying heic2any:", error);

      // Fallback to heic2any
      try {
        console.log("Attempting conversion with heic2any...");
        const result = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 1.0,
        });
        const blob = Array.isArray(result) ? result[0] : result;
        console.log("heic2any conversion successful, blob size:", blob.size);
        return blob;
      } catch (error2: any) {
        console.error("heic2any also failed:", error2);
        throw new Error(
          `Failed to convert HEIC file: ${error2.message || error2.code || "Unknown error"}. This HEIC file format may not be supported by browser-based converters.`,
        );
      }
    }
  };

  // Load image and get dimensions
  const loadImage = async (
    blob: Blob,
  ): Promise<{ img: HTMLImageElement; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ img, width: img.width, height: img.height });
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(blob);
    });
  };

  // Process uploaded files
  const processFiles = async (files: File[]) => {
    setError("");
    setProgress("Processing files...");

    const processed: ProcessedImage[] = [];
    const fileMap = new Map<string, File[]>();

    // Group files by base name (without extension)
    files.forEach((file) => {
      const baseName = file.name.replace(/\.[^/.]+$/, "");
      if (!fileMap.has(baseName)) {
        fileMap.set(baseName, []);
      }
      fileMap.get(baseName)!.push(file);
    });

    // Process each group
    for (const [baseName, groupFiles] of Array.from(fileMap.entries())) {
      try {
        let imageBlob: Blob | null = null;
        let originalFile: File | null = null;

        // Sort by priority: jpg/jpeg/png > heic/heif > mp4/mov
        const sortedFiles = groupFiles.sort((a, b) => {
          const getTypePriority = (file: File) => {
            const ext = file.name.toLowerCase().split(".").pop() || "";
            if (["jpg", "jpeg", "png"].includes(ext)) return 0;
            if (["heic", "heif"].includes(ext)) return 1;
            if (["mp4", "mov"].includes(ext)) return 2;
            return 3;
          };
          return getTypePriority(a) - getTypePriority(b);
        });

        // Try each file until we get valid image data
        for (const file of sortedFiles) {
          const ext = file.name.toLowerCase().split(".").pop() || "";

          try {
            if (["jpg", "jpeg", "png"].includes(ext)) {
              imageBlob = file;
              originalFile = file;
              break;
            } else if (["heic", "heif"].includes(ext)) {
              imageBlob = await convertHeicToBlob(file);
              originalFile = file;
              break;
            } else if (["mp4", "mov"].includes(ext)) {
              const extractedCanvas = await extractVideoFrame(file);
              // eslint-disable-next-line no-loop-func
              imageBlob = await new Promise<Blob>((resolve, reject) => {
                extractedCanvas.toBlob(
                  (blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error("Failed to create blob from canvas"));
                  },
                  "image/jpeg",
                  0.9,
                );
              });
              originalFile = file;
              break;
            }
          } catch (err) {
            console.warn(`Failed to process ${file.name}, trying next file...`);
            continue;
          }
        }

        if (!imageBlob || !originalFile) {
          console.error(`Could not process any file for ${baseName}`);
          continue;
        }

        // Load image and get dimensions
        const { img, width, height } = await loadImage(imageBlob);

        // Create preview
        const previewCanvas = document.createElement("canvas");
        const maxPreviewSize = 200;
        const scale = Math.min(maxPreviewSize / width, maxPreviewSize / height);
        previewCanvas.width = width * scale;
        previewCanvas.height = height * scale;
        const previewCtx = previewCanvas.getContext("2d");
        previewCtx?.drawImage(
          img,
          0,
          0,
          previewCanvas.width,
          previewCanvas.height,
        );
        const preview = previewCanvas.toDataURL("image/jpeg", 0.7);

        // Clean up
        URL.revokeObjectURL(img.src);

        processed.push({
          id: `${Date.now()}-${Math.random()}`,
          file: originalFile,
          imageBlob: imageBlob,
          preview,
          width,
          height,
          originalName: originalFile.name,
        });
      } catch (err) {
        console.error(`Error processing ${baseName}:`, err);
        setError(
          `Error processing ${baseName}: ${err instanceof Error ? err.message : "Unknown error"}. Please try a different file or format.`,
        );
      }
    }

    // Sort into portrait and landscape
    const portrait: ProcessedImage[] = [];
    const landscape: ProcessedImage[] = [];

    processed.forEach((img) => {
      if (img.height >= img.width) {
        portrait.push(img);
      } else {
        landscape.push(img);
      }
    });

    setPortraitImages((prev) => [...prev, ...portrait]);
    setLandscapeImages((prev) => [...prev, ...landscape]);
    setProgress("");
  };

  // Dropzone
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    console.log("=== onDrop called ===");
    console.log("Accepted files:", acceptedFiles);
    console.log("Rejected files:", rejectedFiles);

    if (rejectedFiles.length > 0) {
      console.log(
        "Rejection details:",
        rejectedFiles.map((r) => ({
          name: r.file.name,
          type: r.file.type,
          size: r.file.size,
          errors: r.errors,
        })),
      );
      const rejectedNames = rejectedFiles.map((f) => f.file.name).join(", ");
      const errorDetails = rejectedFiles
        .map((f) => f.errors.map((e: any) => e.message).join(", "))
        .join("; ");
      setError(`Files rejected: ${rejectedNames}. Reason: ${errorDetails}`);
    }
    if (acceptedFiles.length > 0) {
      console.log("Processing accepted files...");
      processFiles(acceptedFiles);
    } else if (rejectedFiles.length === 0) {
      console.warn("No files provided to onDrop");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // No accept prop - let all files through, validator will check extensions
    noClick: false,
    noKeyboard: false,
    multiple: true,
    validator: (file) => {
      console.log("Validator called with file:", file);
      if (!file || !file.name) {
        console.error("File or file.name is undefined:", file);
        return {
          code: "no-filename",
          message: "File has no name",
        };
      }
      const ext = file.name.toLowerCase().split(".").pop() || "";
      const validExts = ["heic", "heif", "jpg", "jpeg", "png", "mp4", "mov"];
      console.log(
        "Validating file:",
        file.name,
        "Extension:",
        ext,
        "Type:",
        file.type,
      );
      if (validExts.includes(ext)) {
        return null; // null means file is valid
      }
      return {
        code: "invalid-file-type",
        message: `File type .${ext} is not supported. Please use HEIC, HEIF, JPG, JPEG, PNG, MP4, or MOV files.`,
      };
    },
  });

  // Remove image
  const removeImage = (id: string, type: "portrait" | "landscape") => {
    if (type === "portrait") {
      const newPortraitImages = portraitImages.filter((img) => img.id !== id);
      setPortraitImages(newPortraitImages);
      // If no more portrait images, reset portrait height
      if (newPortraitImages.length === 0) {
        setPortraitHeight(0);
      }
      // If no images left at all, clear all settings
      if (newPortraitImages.length === 0 && landscapeImages.length === 0) {
        setOutputFormats([]);
        setCustomPrefix("");
        setPreserveFilenames(false);
        setQuality(85);
      }
    } else {
      const newLandscapeImages = landscapeImages.filter((img) => img.id !== id);
      setLandscapeImages(newLandscapeImages);
      // If no more landscape images, reset landscape width
      if (newLandscapeImages.length === 0) {
        setLandscapeWidth(0);
      }
      // If no images left at all, clear all settings
      if (newLandscapeImages.length === 0 && portraitImages.length === 0) {
        setOutputFormats([]);
        setCustomPrefix("");
        setPreserveFilenames(false);
        setQuality(85);
      }
    }
  };

  // Drag and drop between columns
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      // Reordering within same column
      const column =
        source.droppableId === "portrait" ? portraitImages : landscapeImages;
      const newColumn = Array.from(column);
      const [removed] = newColumn.splice(source.index, 1);
      newColumn.splice(destination.index, 0, removed);

      if (source.droppableId === "portrait") {
        setPortraitImages(newColumn);
      } else {
        setLandscapeImages(newColumn);
      }
    } else {
      // Moving between columns
      const sourceColumn =
        source.droppableId === "portrait" ? portraitImages : landscapeImages;
      const destColumn =
        destination.droppableId === "portrait"
          ? portraitImages
          : landscapeImages;
      const newSource = Array.from(sourceColumn);
      const newDest = Array.from(destColumn);
      const [removed] = newSource.splice(source.index, 1);
      newDest.splice(destination.index, 0, removed);

      if (source.droppableId === "portrait") {
        setPortraitImages(newSource);
        setLandscapeImages(newDest);
      } else {
        setLandscapeImages(newSource);
        setPortraitImages(newDest);
      }
    }
  };

  // Convert and download
  const convertAndDownload = async () => {
    if (portraitImages.length === 0 && landscapeImages.length === 0) {
      setError("No images to convert");
      return;
    }

    // Collect all errors
    const errors: string[] = [];

    if (outputFormats.length === 0) {
      errors.push("Please select at least one output format");
    }

    if (portraitImages.length > 0 && portraitHeight === 0) {
      errors.push("Please select at least one target height or width.");
    }

    if (
      landscapeImages.length > 0 &&
      landscapeWidth === 0 &&
      !errors.includes("Please select at least one target height or width.")
    ) {
      errors.push("Please select at least one target height or width.");
    }

    if (errors.length > 0) {
      setError(errors.join("\n"));
      return;
    }

    setIsProcessing(true);
    setProgress("Converting images...");
    setError("");

    try {
      const allImages = [
        ...portraitImages.map((img) => ({
          ...img,
          targetHeight: portraitHeight,
          type: "portrait" as const,
        })),
        ...landscapeImages.map((img) => ({
          ...img,
          targetWidth: landscapeWidth,
          type: "landscape" as const,
        })),
      ];

      // If only one image and one format, download directly without ZIP
      const isSingleImage =
        allImages.length === 1 && outputFormats.length === 1;
      const zip = isSingleImage ? null : new JSZip();

      for (let i = 0; i < allImages.length; i++) {
        const imgData = allImages[i];

        try {
          // Load original image
          let blob: Blob = imgData.file;
          const ext = imgData.file.name.toLowerCase().split(".").pop() || "";

          if (["heic", "heif"].includes(ext)) {
            blob = await convertHeicToBlob(imgData.file);
          } else if (["mp4", "mov"].includes(ext)) {
            const canvas = await extractVideoFrame(imgData.file);
            blob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                (b) => {
                  if (b) resolve(b);
                  else reject(new Error("Failed to create blob"));
                },
                "image/jpeg",
                0.9,
              );
            });
          }

          const { img } = await loadImage(blob);

          // Calculate new dimensions
          let newWidth: number, newHeight: number;
          if (imgData.type === "portrait") {
            const scale = imgData.targetHeight / img.height;
            newHeight = imgData.targetHeight;
            newWidth = Math.round(img.width * scale);
          } else {
            const scale = imgData.targetWidth / img.width;
            newWidth = imgData.targetWidth;
            newHeight = Math.round(img.height * scale);
          }

          // Create canvas and resize
          const canvas = document.createElement("canvas");
          canvas.width = newWidth;
          canvas.height = newHeight;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, newWidth, newHeight);

          // Convert to each selected format
          for (const outputFormat of outputFormats) {
            setProgress(
              `Converting image ${i + 1} of ${allImages.length} to ${outputFormat.toUpperCase()}...`,
            );

            const mimeType = `image/${outputFormat}`;
            const qualityValue = outputFormat === "png" ? 1 : quality / 100;

            const convertedBlob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                (blob) => {
                  if (blob) resolve(blob);
                  else reject(new Error("Failed to convert image"));
                },
                mimeType,
                qualityValue,
              );
            });

            // Generate filename
            let filename: string;
            if (preserveFilenames) {
              const baseName = imgData.originalName
                .replace(/\.[^/.]+$/, "")
                .replace(/\s+/g, "-");
              filename = `${baseName}.${outputFormat}`;
            } else {
              if (customPrefix.trim() === "") {
                // No custom prefix provided, use "optimized" as default
                if (isSingleImage) {
                  filename = `optimized.${outputFormat}`;
                } else {
                  // Multiple images: optimized-01, optimized-02, etc.
                  const paddedNumber = String(i + 1).padStart(2, "0");
                  filename = `optimized-${paddedNumber}.${outputFormat}`;
                }
              } else {
                // Use custom prefix with numbered format and replace spaces with hyphens
                const cleanPrefix = customPrefix.trim().replace(/\s+/g, "-");
                if (isSingleImage) {
                  // Single image with prefix: just use prefix (no number)
                  filename = `${cleanPrefix}.${outputFormat}`;
                } else {
                  // Multiple images with prefix: add numbers
                  const paddedNumber = String(i + 1).padStart(2, "0");
                  filename = `${cleanPrefix}-${paddedNumber}.${outputFormat}`;
                }
              }
            }

            // Add to zip with correct local timestamp
            const now = new Date();
            const timezoneOffset = now.getTimezoneOffset() * 60000;
            const localTimeAsUTC = new Date(now.getTime() - timezoneOffset);

            // Download single image directly or add to ZIP
            if (isSingleImage) {
              const url = URL.createObjectURL(convertedBlob);
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } else {
              zip!.file(filename, convertedBlob, { date: localTimeAsUTC });
            }
          }

          URL.revokeObjectURL(img.src);
        } catch (err) {
          console.error(`Error converting ${imgData.originalName}:`, err);
        }
      }

      // Generate and download ZIP if multiple images
      if (!isSingleImage) {
        setProgress("Creating ZIP file...");
        const zipBlob = await zip!.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "converted-images.zip";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      setProgress("Download complete!");
      setTimeout(() => setProgress(""), 3000);
    } catch (err) {
      setError(
        "Error during conversion: " +
          (err instanceof Error ? err.message : "Unknown error"),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="App">
      {/* Update Notice */}
      {showUpdateNotice && (
        <div onClick={handleReload} className="update-notice">
          <strong>New version available!</strong> Click to reload.
        </div>
      )}

      <header className="header">
        <h1>OptimizeMyPics</h1>
        <p>Convert and resize HEIC, HEIF, MP4, MOV, JPG, PNG images</p>
      </header>

      <main className="main">
        {/* Step 1 */}
        <div className="step-header">
          <div className="step-number">01</div>
          <div className="step-content">
            <h3>Upload Files</h3>
            <p>
              Upload all file types at once. Images are auto-sorted by
              orientation, and videos use their first frame as image.
            </p>
          </div>
        </div>

        {/* Upload Zone */}
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? "active" : ""}`}
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p>
              {isDragActive
                ? "Drop files here"
                : "Drag & drop all files here, or click to select"}
            </p>
            <small>Supports: HEIC, HEIF, MP4, MOV, JPG, JPEG, PNG</small>
          </div>
        </div>

        {progress && <div className="progress">{progress}</div>}

        {/* Step 2 */}
        <div className="step-header">
          <div className="step-number">02</div>
          <div className="step-content">
            <h3>Set Dimensions</h3>
            <p>
              Choose a preset size or enter custom dimensions. Drag images
              between columns or remove as needed.
            </p>
          </div>
        </div>

        {/* Two Column Layout */}
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="columns">
            {/* Portrait Column */}
            <div className="column">
              <div className="column-header">
                <div className="column-title-row">
                  <h2>Portrait (Height ≥ Width)</h2>
                  {portraitImages.length > 0 && (
                    <button
                      className="clear-column-btn"
                      onClick={() => {
                        setPortraitImages([]);
                        setPortraitHeight(0);
                      }}
                      disabled={isProcessing}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="control">
                  <label>Target Height (px):</label>
                  <div className="dimension-controls">
                    <div className="dimension-button-group">
                      <button
                        type="button"
                        className={`dimension-btn ${portraitHeight === 500 ? "selected" : ""}`}
                        onClick={() => setPortraitHeight(500)}
                      >
                        500px
                      </button>
                      <button
                        type="button"
                        className={`dimension-btn ${portraitHeight === 1000 ? "selected" : ""}`}
                        onClick={() => setPortraitHeight(1000)}
                      >
                        1000px
                      </button>
                      <button
                        type="button"
                        className={`dimension-btn ${portraitHeight === 1500 ? "selected" : ""}`}
                        onClick={() => setPortraitHeight(1500)}
                      >
                        1500px
                      </button>
                      <button
                        type="button"
                        className={`dimension-btn ${portraitHeight === 2000 ? "selected" : ""}`}
                        onClick={() => setPortraitHeight(2000)}
                      >
                        2000px
                      </button>
                      <input
                        type="number"
                        className="dimension-input"
                        value={
                          [500, 1000, 1500, 2000].includes(portraitHeight)
                            ? ""
                            : portraitHeight === 0
                              ? ""
                              : portraitHeight
                        }
                        onChange={(e) =>
                          setPortraitHeight(parseInt(e.target.value) || 0)
                        }
                        min="1"
                        placeholder="Custom"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Droppable droppableId="portrait">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`image-grid ${snapshot.isDraggingOver ? "drag-over" : ""}`}
                  >
                    {portraitImages.map((img, index) => (
                      <Draggable
                        key={img.id}
                        draggableId={img.id}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="image-item"
                          >
                            <button
                              className="remove-btn"
                              onClick={() => removeImage(img.id, "portrait")}
                            >
                              ×
                            </button>
                            <img src={img.preview} alt={img.originalName} />
                            <div className="image-info">
                              <small>{img.originalName}</small>
                              <small>
                                {img.width} × {img.height}
                              </small>
                            </div>
                            {estimatedSizes.has(img.id) && (
                              <div className="image-size-estimate">
                                {estimatedSizes.get(img.id)!.map((s, i) => (
                                  <small key={s.format}>
                                    {estimatedSizes.get(img.id)!.length > 1 ? `${s.format}: ` : ''}~{formatFileSize(s.size)}
                                    {i < estimatedSizes.get(img.id)!.length - 1 ? ' · ' : ''}
                                  </small>
                                ))}
                              </div>
                            )}
                            {isEstimating && !estimatedSizes.has(img.id) && outputFormats.length > 0 && portraitHeight > 0 && (
                              <div className="image-size-estimate estimating">
                                <small>Estimating...</small>
                              </div>
                            )}
                            {!estimatedSizes.has(img.id) && !isEstimating && (outputFormats.length === 0 || portraitHeight === 0) && (
                              <div className="image-size-estimate hint">
                                <small>Select target height or width & output format (below) to see estimated size</small>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            {/* Landscape Column */}
            <div className="column">
              <div className="column-header">
                <div className="column-title-row">
                  <h2>Landscape (Width &gt; Height)</h2>
                  {landscapeImages.length > 0 && (
                    <button
                      className="clear-column-btn"
                      onClick={() => {
                        setLandscapeImages([]);
                        setLandscapeWidth(0);
                      }}
                      disabled={isProcessing}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="control">
                  <label>Target Width (px):</label>
                  <div className="dimension-controls">
                    <div className="dimension-button-group">
                      <button
                        type="button"
                        className={`dimension-btn ${landscapeWidth === 500 ? "selected" : ""}`}
                        onClick={() => setLandscapeWidth(500)}
                      >
                        500px
                      </button>
                      <button
                        type="button"
                        className={`dimension-btn ${landscapeWidth === 1000 ? "selected" : ""}`}
                        onClick={() => setLandscapeWidth(1000)}
                      >
                        1000px
                      </button>
                      <button
                        type="button"
                        className={`dimension-btn ${landscapeWidth === 1500 ? "selected" : ""}`}
                        onClick={() => setLandscapeWidth(1500)}
                      >
                        1500px
                      </button>
                      <button
                        type="button"
                        className={`dimension-btn ${landscapeWidth === 2000 ? "selected" : ""}`}
                        onClick={() => setLandscapeWidth(2000)}
                      >
                        2000px
                      </button>
                      <input
                        type="number"
                        className="dimension-input"
                        value={
                          [500, 1000, 1500, 2000].includes(landscapeWidth)
                            ? ""
                            : landscapeWidth === 0
                              ? ""
                              : landscapeWidth
                        }
                        onChange={(e) =>
                          setLandscapeWidth(parseInt(e.target.value) || 0)
                        }
                        min="1"
                        placeholder="Custom"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Droppable droppableId="landscape">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`image-grid ${snapshot.isDraggingOver ? "drag-over" : ""}`}
                  >
                    {landscapeImages.map((img, index) => (
                      <Draggable
                        key={img.id}
                        draggableId={img.id}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="image-item"
                          >
                            <button
                              className="remove-btn"
                              onClick={() => removeImage(img.id, "landscape")}
                            >
                              ×
                            </button>
                            <img src={img.preview} alt={img.originalName} />
                            <div className="image-info">
                              <small>{img.originalName}</small>
                              <small>
                                {img.width} × {img.height}
                              </small>
                            </div>
                            {estimatedSizes.has(img.id) && (
                              <div className="image-size-estimate">
                                {estimatedSizes.get(img.id)!.map((s, i) => (
                                  <small key={s.format}>
                                    {estimatedSizes.get(img.id)!.length > 1 ? `${s.format}: ` : ''}~{formatFileSize(s.size)}
                                    {i < estimatedSizes.get(img.id)!.length - 1 ? ' · ' : ''}
                                  </small>
                                ))}
                              </div>
                            )}
                            {isEstimating && !estimatedSizes.has(img.id) && outputFormats.length > 0 && landscapeWidth > 0 && (
                              <div className="image-size-estimate estimating">
                                <small>Estimating...</small>
                              </div>
                            )}
                            {!estimatedSizes.has(img.id) && !isEstimating && (outputFormats.length === 0 || landscapeWidth === 0) && (
                              <div className="image-size-estimate hint">
                                <small>Select target height or width & output format (below) to see estimated size</small>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </DragDropContext>

        {/* Error message above Step 3 */}
        {error && <div className="error">{error}</div>}

        {/* Step 3 */}
        <div className="step-header">
          <div className="step-number">03</div>
          <div className="step-content">
            <h3>Configure & Download</h3>
            <p>
              Customize conversion settings and choose output formats, then
              download your optimized images.
            </p>
          </div>
        </div>

        {/* Conversion Controls - Full Width at Bottom */}
        <div className="conversion-controls">
          <h2>Conversion Settings</h2>

          <div className="controls-row">
            <div className="control">
              <label>Custom Prefix (optional):</label>
              <div className="control checkbox inline small">
                <label>
                  <input
                    type="checkbox"
                    checked={preserveFilenames}
                    onChange={(e) => setPreserveFilenames(e.target.checked)}
                  />
                  Keep original filenames
                </label>
              </div>
              <input
                type="text"
                className="small-input"
                value={customPrefix}
                onChange={(e) => setCustomPrefix(e.target.value)}
                placeholder="e.g., 'Smith' creates 'Smith-01, Smith-02…'"
                disabled={preserveFilenames}
              />
            </div>

            <div className="control">
              <label>Quality: {quality}%</label>
              <input
                type="range"
                min="1"
                max="100"
                value={quality}
                onChange={(e) => setQuality(parseInt(e.target.value))}
                disabled={
                  outputFormats.includes("png") && outputFormats.length === 1
                }
              />
              <small className="help-text">
                Adjust compression quality (higher = better quality, larger file
                size)
              </small>
            </div>
          </div>

          <div className="controls-row">
            <div className="control">
              <label>Output Format:</label>
              <div className="format-button-group">
                <button
                  type="button"
                  className={`format-btn ${outputFormats.includes("webp") ? "selected" : ""}`}
                  onClick={() => toggleFormat("webp")}
                >
                  WebP
                </button>
                <button
                  type="button"
                  className={`format-btn ${outputFormats.includes("jpeg") ? "selected" : ""}`}
                  onClick={() => toggleFormat("jpeg")}
                >
                  JPEG
                </button>
                <button
                  type="button"
                  className={`format-btn ${outputFormats.includes("png") ? "selected" : ""}`}
                  onClick={() => toggleFormat("png")}
                >
                  PNG
                </button>
              </div>
              <small className="help-text">
                Select one or more formats. WebP: best compression, JPEG: most
                compatible, PNG: transparent backgrounds
              </small>
            </div>
          </div>

          <div className="button-group">
            <button
              className={`convert-btn ${!(portraitImages.length > 0 || landscapeImages.length > 0) ? "disabled" : ""}`}
              onClick={convertAndDownload}
              disabled={
                isProcessing ||
                !(portraitImages.length > 0 || landscapeImages.length > 0)
              }
            >
              {isProcessing ? "Hang tight…" : "Download Images"}
            </button>
            {(portraitImages.length > 0 ||
              landscapeImages.length > 0 ||
              portraitHeight > 0 ||
              landscapeWidth > 0 ||
              outputFormats.length > 0) && (
              <button
                className="clear-btn"
                onClick={() => {
                  setPortraitImages([]);
                  setLandscapeImages([]);
                  setPortraitHeight(0);
                  setLandscapeWidth(0);
                  setOutputFormats([]);
                  setQuality(85);
                  setPreserveFilenames(false);
                  setCustomPrefix("");
                  setError("");
                  setProgress("");
                }}
                disabled={isProcessing}
              >
                Clear All
              </button>
            )}
          </div>
          <small className="help-text center privacy-note">
            Please note, your data is secure. All files are processed directly
            in your browser and then removed. Nothing will be stored by this
            app.
          </small>
        </div>
      </main>

      <footer className="footer">
        <p>© 2026 Kickstart Creatives. All rights reserved.</p>
        <p>
          Have a suggestion or feedback?{" "}
          <button
            onClick={() => setShowContactModal(true)}
            className="footer-link"
          >
            Let us know
          </button>
        </p>
      </footer>

      {/* Contact Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </div>
  );
}

export default App;
